import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import SvgIcon from "@mui/material/SvgIcon";
import {Code, Folder, Search, Tag} from 'lucide-react';
import {
    alpha,
    Box,
    Button,
    Chip,
    Divider,
    InputAdornment,
    List,
    Pagination,
    PaginationItem,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {ReactComponent as GithubIcon} from '../images/github.svg';
import {AuthContext} from "./AuthContext";
import {TradingAlgoListProps, TradingAlgoStored} from "../utils/typesTrading";
import {formatDateTime} from "../utils/datetime";


export const TradingAlgoList: React.FC<TradingAlgoListProps> = (props) => {
    const [selectedAlgoId, setSelectedAlgoId] = useState<string | null>(null);
    const authContext = useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [numPages, setNumPages] = useState<number>(1);
    const theme = useTheme();
    const [algos, setAlgos] = useState<TradingAlgoStored[]>(
        (props.algos || []).sort((a, b) =>
            new Date(b.last_modified_datetime_utc).getTime() - new Date(a.last_modified_datetime_utc).getTime()
        )
    );
    const [selectedAlgo, setSelectedAlgo] = useState<TradingAlgoStored | null>(null);

    const filteredGists = algos.filter(algo=>
        // Filter gists based on search term

        (algo.strategy_name && algo.strategy_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (algo.tag && algo.tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleCreateNewAlgorithm = () => {
        setSelectedAlgoId(null);
        props.onCreateNewAlgo()
    }

    const getSourceIcon = (source: 'github' | 'junkyard' | '') => {
        // Get appropriate icon for source
        // https://simpleicons.org
        // https://lucide.dev
        switch (source) {
            case 'github':
                return <SvgIcon component={GithubIcon} sx={{ height: 16, width: 16 }} />;
            case 'junkyard':
                return <Code size={16} className="text-blue-600" />;
            default:
                return <Code size={16} className="text-gray-600" />;
        }
    };

    useEffect(() => {
        async function selectedAlgoChanged() {
            let _selectedAlgo = undefined;
            for (let algo of algos) {
                if (algo.id === selectedAlgoId) {
                    setSelectedAlgo(algo)
                    _selectedAlgo = algo;
                    break;
                }
            }
            if (!_selectedAlgo) {
                return;
            }
            try {
                if (props.selectedAlgoChanged !== undefined) {
                    console.info('Selecting algo with id ', _selectedAlgo.id);
                    props.selectedAlgoChanged(_selectedAlgo);
                } else {
                    console.error('No selected algo changed function to call.')
                }
            } catch (error) {
                console.error(error);
            }
        }
        selectedAlgoChanged();
    }, [selectedAlgoId])

    useEffect(() => {
        async function setNewAlgoData() {
            const sortedAlgos = (props.algos || []).sort((a, b) =>
                new Date(b.last_modified_datetime_utc).getTime() - new Date(a.last_modified_datetime_utc).getTime()
            );
            setAlgos(sortedAlgos);
            if (props.numPages !== null) {
                setNumPages(props.numPages);
            }
        }
        setNewAlgoData();
    }, [props])


    // Get tag color based on programming language/type
    const getTagColor = (tag: string): string => {
        const colors: { [key: string]: string } = {
            'TypeScript': 'bg-blue-100 text-blue-800',
            'JavaScript': 'bg-yellow-100 text-yellow-800',
            'Python': 'bg-green-100 text-green-800',
            'CSS': 'bg-pink-100 text-pink-800',
            'HTML': 'bg-orange-100 text-orange-800',
            'SQL': 'bg-purple-100 text-purple-800',
            'JSON': 'bg-gray-100 text-gray-800',
            'YAML': 'bg-red-100 text-red-800'
        };
        return colors[tag] || 'bg-gray-100 text-gray-800';
    };


    const renderEmptyState = () => {
        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: 'center',
                    color: 'text.secondary',
                }}
            >
                <Search size={32} style={{ margin: '0 auto 12px auto', display: 'block', color: '#bdbdbd' }} />
                <Typography variant="body2">
                    Nothing here yet
                </Typography>
            </Box>
        );
    };

    const renderAlgoItem = (algo: TradingAlgoStored) => {
        return (
            <Paper
                key={algo.id}
                onClick={() => setSelectedAlgoId(algo.id)}
                elevation={0}
                sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    borderLeft: selectedAlgoId === algo.id ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                    backgroundColor: selectedAlgoId === algo.id
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: selectedAlgoId === algo.id
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.action.hover, 0.04),
                    },
                    '&:last-child': {
                        borderBottom: 'none',
                    }
                }}
            >
                {/* Top row: File name and source icon */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5}}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 500,
                            color: theme.palette.info.contrastText,
                            lineHeight: 1.3,
                            pr: 1,
                            flex: 1,
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                        }}
                    >
                        {algo.strategy_name}
                    </Typography>
                    <Box sx={{flexShrink: 0}}>
                        {getSourceIcon(selectedAlgo?.source || '')}
                    </Box>
                </Box>

                {/* Bottom row: Folder/tag and date */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}>
                    {/*<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>*/}
                    {/*    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>*/}
                    {/*        <Folder size={12} color={theme.palette.text.secondary}/>*/}
                    {/*        <Typography*/}
                    {/*            variant="caption"*/}
                    {/*            color="text.secondary"*/}
                    {/*            sx={{*/}
                    {/*                maxWidth: 100,*/}
                    {/*                overflow: 'hidden',*/}
                    {/*                textOverflow: 'ellipsis',*/}
                    {/*                whiteSpace: 'nowrap',*/}
                    {/*                display: 'block',*/}
                    {/*            }}*/}
                    {/*        >*/}
                    {/*            {algo.folder}*/}
                    {/*        </Typography>*/}
                    {/*    </Box>*/}
                    {/*    {algo.tag && (*/}
                    {/*        <Chip*/}
                    {/*            icon={<Tag size={10}/>}*/}
                    {/*            label={algo.tag}*/}
                    {/*            size="small"*/}
                    {/*            variant="outlined"*/}
                    {/*            sx={{*/}
                    {/*                height: 20,*/}
                    {/*                fontSize: '0.75rem',*/}
                    {/*                '& .MuiChip-label': {*/}
                    {/*                    px: 0.5,*/}
                    {/*                },*/}
                    {/*                '& .MuiChip-icon': {*/}
                    {/*                    fontSize: '0.625rem',*/}
                    {/*                    ml: 0.5,*/}
                    {/*                    mr: -0.25,*/}
                    {/*                },*/}
                    {/*            }}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</Box>*/}
                    <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                        {formatDateTime(algo.last_modified_datetime_utc)}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <React.Fragment>
            <Paper
                elevation={0}
                sx={{
                    width: { xs: '100%', md: '25%' },
                    minWidth: { md: 320 },
                    height: { md: '90vh' },
                    flex: { xs: '1 1 auto', md: '0 0 auto' },
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: 1,
                    borderColor: 'divider',
                    borderRadius: 0,
                }}
            >
                {/* Header */}
                <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'transparent' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Folder />
                        Strategies
                    </Typography>

                    {/* Search Bar */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1 }}
                    />

                    <Typography variant="caption" color="text.secondary">
                        {filteredGists.length} of {algos.length} items
                    </Typography>


                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
                        <Pagination
                            count={numPages}
                            variant={'outlined'}
                            onChange={(event, page) => {props.onPageChanged(page);}}
                            renderItem={(item) => {
                                if (item.type === 'first' || item.type === 'last' ||
                                    item.type === 'previous' || item.type === 'next') {
                                    return <PaginationItem {...item} />;
                                }
                                return null;
                            }}
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPagination-ul': {
                                    flexWrap: 'nowrap',
                                }
                            }}
                        ></Pagination>
                    </Box>
                </Box>

                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'transparent' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            onClick={handleCreateNewAlgorithm}
                        >
                            New Strategy
                        </Button>
                    </Box>
                </Box>

                {/* Scrollable List */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {filteredGists.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <List disablePadding>
                            {filteredGists.map((algo, index) => (
                                <React.Fragment key={algo.id}>
                                    {renderAlgoItem(algo)}
                                    {index < filteredGists.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Paper>
        </React.Fragment>
    );
};
